import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { ChartMetric } from "../types/distributor_sales"

/** Shared palette and pieces for `DistributorSalesInteractivePie` (table hierarchy modal + SKU card). */
export const PIE_COLORS = [
  "#2563eb", "#16a34a", "#ca8a04", "#9333ea", "#dc2626",
  "#0891b2", "#ea580c", "#db2777", "#4f46e5", "#059669",
  "#7c3aed", "#0284c7",
]
export const OTHER_COLOR = "#94a3b8"

export function formatPieMetricValue(value: number, metric: ChartMetric): string {
  if (metric.includes("vendor_cost") || metric.includes("list_wholesale")) {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
    return `$${value.toFixed(0)}`
  }
  if (metric === "stores_ordered") {
    return `${Math.round(value).toLocaleString()} stores`
  }
  return value.toLocaleString()
}

/** Metric options for the pie dropdowns, in display order. */
export const PIE_METRIC_OPTIONS: { value: ChartMetric; label: string }[] = [
  { value: "shipped_vendor_cost", label: "Shipped (vendor cost)" },
  { value: "shipped_list_wholesale", label: "Shipped (list wholesale)" },
  { value: "ordered_vendor_cost", label: "Ordered (vendor cost)" },
  { value: "ordered_list_wholesale", label: "Ordered (list wholesale)" },
  { value: "shipped_quantity", label: "Shipped (Qty)" },
  { value: "ordered_quantity", label: "Ordered (Qty)" },
  { value: "stores_ordered", label: "Store count" },
]

export function pieMetricLabel(metric: ChartMetric): string {
  return PIE_METRIC_OPTIONS.find((o: { value: ChartMetric }) => o.value === metric)?.label ?? metric
}

/**
 * Metrics with at least one non-zero value across `rows`. Sources report different measures
 * (McLane has no orders or list wholesale), so offering every metric leads to empty charts.
 * `current` is always kept so the dropdown never loses its selected value.
 */
export function availablePieMetrics<T>(
  rows: T[],
  get_value: (row: T, metric: ChartMetric) => number,
  current: ChartMetric,
): ChartMetric[] {
  return PIE_METRIC_OPTIONS
    .map((o: { value: ChartMetric }) => o.value)
    .filter((metric: ChartMetric) => metric === current || rows.some((row: T) => Boolean(get_value(row, metric))))  // API sends null for absent metrics
}

interface PieMetricSelectProps {
  value: ChartMetric
  onChange: (metric: ChartMetric) => void
  metrics?: ChartMetric[]
  className?: string
}

export function PieMetricSelect({ value, onChange, metrics, className }: PieMetricSelectProps) {
  const options = PIE_METRIC_OPTIONS.filter((o: { value: ChartMetric }) => !metrics || metrics.includes(o.value))
  return (
    <div class={className ?? "w-40"}>
      <Select value={value} onValueChange={(v: string) => onChange(v as ChartMetric)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o: { value: ChartMetric; label: string }) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function sliceFill(name: string, index: number): string {
  return name === "Other" ? OTHER_COLOR : PIE_COLORS[index % PIE_COLORS.length]
}

/** Modal pie: tighter radius when many slices. Use `size: "card"` for the dashboard SKU card (slightly larger). */
export function pieOuterRadius(sliceCount: number, size: "modal" | "card" = "modal"): number {
  if (size === "card") {
    if (sliceCount <= 8) return 110
    if (sliceCount <= 16) return 100
    return 90
  }
  if (sliceCount <= 8) return 100
  if (sliceCount <= 16) return 90
  return 80
}

/** Percent label inside slice (hidden for very small slices). */
export function PieSlicePercentLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) {
  if (percent < 0.04) return null
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" class="text-xs font-medium">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}

export function cellFillOpacity(sliceIndex: number, emphasisIndex: number | null): number {
  if (emphasisIndex === null) return 1
  return sliceIndex === emphasisIndex ? 1 : 0.28
}

/** Same content Recharts’ pie tooltip showed; shown when hovering a slice or a legend row (`hoverIndex`). */
interface DistributorSalesPieSliceTooltipProps {
  slice: { name: string; value: number } | undefined | null
  metric: ChartMetric
  total: number
}

export function DistributorSalesPieSliceTooltip({ slice, metric, total }: DistributorSalesPieSliceTooltipProps) {
  if (!slice) return null
  const pctLabel = total > 0 ? `${((slice.value / total) * 100).toFixed(1)}%` : ""
  return (
    <div class="absolute left-1/2 top-2 z-20 -translate-x-1/2 pointer-events-none max-w-[min(100%,240px)]">
      <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p class="font-semibold text-sm">{slice.name}</p>
        <p class="text-sm text-gray-600">{formatPieMetricValue(slice.value, metric)}</p>
        {pctLabel ? <p class="text-xs text-gray-400">{pctLabel}</p> : null}
      </div>
    </div>
  )
}

interface DistributorSalesPieLegendProps {
  slices: { name: string; value: number }[]
  metric: ChartMetric
  total: number
  emphasisIndex: number | null
  onRowPointerEnter: (index: number) => void
  onRowPointerLeave: () => void
  onRowClick: (index: number) => void
  /** Tailwind max-height for scroll (modal vs card). */
  listMaxHeightClass?: string
  className?: string
}

export function DistributorSalesPieLegend({
  slices,
  metric,
  total,
  emphasisIndex,
  onRowPointerEnter,
  onRowPointerLeave,
  onRowClick,
  listMaxHeightClass = "max-h-[min(52vh,28rem)]",
  className = "",
}: DistributorSalesPieLegendProps) {
  function handleLegendPointerLeave(e: PointerEvent) {
    const next = e.relatedTarget as Node | null
    if (next && (e.currentTarget as HTMLElement).contains(next)) return
    onRowPointerLeave()
  }

  return (
    <div
      class={`flex-1 min-h-0 min-w-0 border border-gray-100 rounded-lg bg-gray-50/50 ${className}`}
      onPointerLeave={handleLegendPointerLeave}
    >
      <ul class={`${listMaxHeightClass} overflow-y-auto overscroll-contain p-3 space-y-2 text-xs`}>
        {slices.map((entry: { name: string; value: number }, idx: number) => {
          const pct = total > 0 ? (entry.value / total) * 100 : 0
          const emphasized = emphasisIndex === idx
          return (
            <li key={`${entry.name}-${idx}`}>
              <button
                type="button"
                class={`w-full flex gap-2 items-start text-left rounded-md px-1 py-0.5 -mx-1 transition-colors ${
                  emphasized ? "bg-blue-50 ring-1 ring-blue-200/80" : "hover:bg-gray-100/80"
                }`}
                onPointerEnter={() => onRowPointerEnter(idx)}
                onClick={() => onRowClick(idx)}
              >
                <span
                  class="w-3 h-3 rounded-sm shrink-0 mt-0.5"
                  style={{ backgroundColor: sliceFill(entry.name, idx) }}
                />
                <span class="text-gray-800 break-words flex-1 min-w-0">{entry.name}</span>
                <span class="text-gray-500 shrink-0 tabular-nums text-right">
                  {formatPieMetricValue(entry.value, metric)}
                  <span class="text-gray-400 ml-1">({pct.toFixed(1)}%)</span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe("cellFillOpacity", () => {
    it("dims non-emphasized slices when an index is active", () => {
      expect(cellFillOpacity(0, 1)).toBe(0.28)
      expect(cellFillOpacity(1, 1)).toBe(1)
    })
    it("shows full opacity when nothing is emphasized", () => {
      expect(cellFillOpacity(0, null)).toBe(1)
    })
  })
}
